import { TrainIdentifier } from "@vygruppen/vy-train-map";
import { Preview } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/infrastructureEventModal/Preview";
import { Prognosis } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/infrastructureEventModal/Prognosis";
import { DropsLogTextInput } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/DropsLogTextInput";
import { SubTypeInputs } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/subTypeInputs";
import { SelectIncidentsForTrain } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/subTypeInputs/IncidentsForTrain";
import {
  SingleTrainFormSchema,
  TrainFormSchema,
} from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/types/formSchema";
import { GroupedInformationResponse } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/useInfrastructureInformationForTrain";
import { useTrainInfoPreview } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/useTrainInfoPreview";
import { formStateHasCustomField } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/utils";
import { TrainEventTypeEnum } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/types/trainEventTypeEnum";
import { supportsMultipleEvents } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/utils";
import { FormFieldWithCustomInput } from "features/CenterContent/shared/operationalInformation/components/FormFieldWithCustomInput";
import { OperationalInformationTypes } from "features/CenterContent/shared/operationalInformation/hooks/useOperationalInformationTypes";
import { filterOpInfoTypesTrain } from "features/CenterContent/shared/operationalInformation/utils";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import {
  DropsBadge,
  DropsStaticAlert,
  Stack,
  Text,
} from "shared/components/Spor";
import { useCountryCodeForDropsRole } from "shared/hooks/useCountryCodeForDropsRole";
import { countryCode as countryCodeEnum } from "shared/types/countryCode";
import { getTrainCustomInputs } from "shared/types/form";
import { useDropsRole } from "stores/useDropsRole";

type TrainInfoFormProps = {
  selectedTrain: TrainIdentifier;
  trainInfoWithOpenState: string[];
  opInfoTypes: OperationalInformationTypes;
  infrastructureData: GroupedInformationResponse | undefined;
  currentFormValues: TrainFormSchema;
  editingEventType?: TrainEventTypeEnum;
};

export type CommonTrainInfoFormProps = {
  mode: "edit" | "create";
  uuid?: string;
};

export const BadgeText = ({
  selectedTrain,
}: {
  selectedTrain: TrainIdentifier;
}) => (
  <Text variant="sm">
    Tog:
    <DropsBadge
      borderRadius="9px"
      paddingY="3px"
      marginX="3px"
      semantics="success"
    >
      {selectedTrain.identifier}
    </DropsBadge>
  </Text>
);

const TrainInfoForm: FC<CommonTrainInfoFormProps & TrainInfoFormProps> = ({
  mode,
  uuid,
  selectedTrain,
  trainInfoWithOpenState,
  opInfoTypes,
  infrastructureData,
  currentFormValues,
  editingEventType,
}) => {
  const { isDriftledningen } = useDropsRole();
  const { events, reasons, actions } = filterOpInfoTypesTrain(opInfoTypes);

  const { previewData, previewStatus } = useTrainInfoPreview({
    train: selectedTrain,
    formValues: currentFormValues,
  });

  const { t } = useTranslation();

  const trainCustomInputs = getTrainCustomInputs(t);

  const countryCode = useCountryCodeForDropsRole();

  return (
    <Stack gap="30px">
      <BadgeText selectedTrain={selectedTrain} />
      <Stack gap="12px">
        {infrastructureData && (
          <Stack marginBottom="18px" gap="12px">
            <SelectIncidentsForTrain items={infrastructureData} />
          </Stack>
        )}
        <FormFieldWithCustomInput<SingleTrainFormSchema>
          items={events}
          {...trainCustomInputs.event}
          optionDisabledPredicate={(event) =>
            editingEventType !== (event.type as TrainEventTypeEnum) && // Ignores the event.type of the edited train
            !supportsMultipleEvents(event) &&
            trainInfoWithOpenState?.some((info) => info === event.type)
          }
        />
        <FormFieldWithCustomInput<SingleTrainFormSchema>
          items={reasons}
          {...trainCustomInputs.reason}
        />
        <FormFieldWithCustomInput<SingleTrainFormSchema>
          items={actions}
          {...trainCustomInputs.action}
        />
        {countryCode === countryCodeEnum.enum.SE && (
          <Prognosis formPrefix="trainForm" />
        )}
      </Stack>

      {currentFormValues?.type && (
        <Stack>
          <SubTypeInputs
            type={currentFormValues?.type}
            mode={mode}
            trainId={selectedTrain}
            uuid={uuid}
          />

          {currentFormValues.type !== TrainEventTypeEnum.TRAIN_CUSTOM &&
            !isDriftledningen() && (
              <DropsLogTextInput
                previewText={previewData?.internalMessage ?? ""}
              />
            )}
        </Stack>
      )}

      <Preview previewStatus={previewStatus} previewData={previewData} />

      {formStateHasCustomField(currentFormValues) && (
        <DropsStaticAlert variant="info" id="infoBoxActionModal">
          {t(
            "Hendelsen inneholder egendefinert tekst. Sjekk at oppsummeringen ser riktig ut før du går videre.",
          )}
        </DropsStaticAlert>
      )}
    </Stack>
  );
};

export { TrainInfoForm };
